import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { readSseStream } from './realtime/sse-stream';
import { environment } from '../../../environments/environment';
import { CompanyContextService } from '@core/context/company-context.service';
import { ApiResponse } from '@core/interfaces/apiResponse.interfaces';
import { ProductNotificationsService } from '@core/services/product-notifications.service';
import { SubscriptionFeaturesService } from '@core/services/subscription-features.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { AlertService } from '@core/services/alert.service';
import { NotificationSoundService } from '@core/services/notification-sound.service';
import { EmployeeTask } from '@models/employee-attendance.interface';

type EmployeeTaskRealtimeEvent = {
  type: 'connected' | 'task.created' | 'task.updated' | 'stock.empty';
  task?: EmployeeTask;
  stock?: { product_name: string; product_business_id: number; on_hand: number; min_stock?: number };
};

export const EMPLOYEE_TASK_CREATED_EVENT = 'employee-task-created';
export const EMPLOYEE_TASK_UPDATED_EVENT = 'employee-task-updated';

@Injectable({ providedIn: 'root' })
export class EmployeeTaskRealtimeService {
  private readonly http = inject(HttpClient);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly tenantSessionService = inject(TenantSessionService);
  private readonly productNotificationsService = inject(ProductNotificationsService);
  private readonly subscriptionFeaturesService = inject(SubscriptionFeaturesService);
  private readonly alertService = inject(AlertService);
  private readonly notificationSoundService = inject(NotificationSoundService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private streamController: AbortController | null = null;
  private connectionVersion = 0;
  private reconnectTimer: number | null = null;

  constructor() {
    this.companyContextService.company$
      .pipe(
        map((company) => `${company?.id ?? ''}:${this.tenantSessionService.getAuthToken() ?? ''}`),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.reconnect());
    this.destroyRef.onDestroy(() => this.close());
  }

  private reconnect(): void {
    this.close();
    if (!this.companyContextService.value?.id || !this.tenantSessionService.getAuthToken() || typeof fetch === 'undefined') return;
    const version = this.connectionVersion;
    const controller = new AbortController();
    this.streamController = controller;
    void this.openStream(version, controller);
  }

  private async openStream(version: number, controller: AbortController): Promise<void> {
    try {
      await firstValueFrom(this.subscriptionFeaturesService.load().pipe(takeUntilDestroyed(this.destroyRef)));
      if (version !== this.connectionVersion || !this.subscriptionFeaturesService.isEnabled('employees.attendance')) return;

      // HttpClient renews expired credentials through the authentication interceptor.
      const response = await firstValueFrom(this.http.get<ApiResponse<EmployeeTask[]>>(
        `${environment.api_rest_url}/employeeAttendance/tasks/me`,
      ).pipe(takeUntilDestroyed(this.destroyRef)));
      if (version !== this.connectionVersion) return;
      const token = this.tenantSessionService.getAuthToken();
      if (!token) return;
      const stream = await fetch(`${environment.api_rest_url}/employeeAttendance/tasks/me/stream`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit',
        redirect: 'error',
      });
      if (!stream.ok || !stream.body || !stream.headers.get('content-type')?.includes('text/event-stream')) {
        throw new Error('SSE connection failed');
      }
      if (version !== this.connectionVersion) return;
      this.addPendingTaskNotifications(response.data ?? []);
      await readSseStream(stream.body, data => {
        if (version === this.connectionVersion) this.handleMessage(data);
      });
    } catch {
      // Network/server failures reconnect; aborted or replaced sessions do not.
    } finally {
      controller.abort();
      if (version === this.connectionVersion && !this.destroyRef.destroyed &&
          this.tenantSessionService.getAuthToken() &&
          this.subscriptionFeaturesService.isEnabled('employees.attendance')) {
        this.reconnectTimer = window.setTimeout(() => this.reconnect(), 30000);
      }
    }
  }

  private addPendingTaskNotifications(tasks: EmployeeTask[]): void {
    tasks.filter(task => task.status === 'pending' || task.status === 'in_progress').forEach(task => {
      this.productNotificationsService.addTaskNotification({
        id: `EMPLOYEE_TASK_ASSIGNED:${task.id}`,
        type: 'EMPLOYEE_TASK_ASSIGNED',
        taskId: task.id,
        title: 'Nueva tarea asignada',
        message: task.title,
        actionLabel: 'Ver mis tareas',
        routeCommands: ['/my-tasks'],
      });
    });
  }

  private handleMessage(data: string): void {
    let event: EmployeeTaskRealtimeEvent | null = null;
    try {
      event = JSON.parse(data) as EmployeeTaskRealtimeEvent;
    } catch {
      return;
    }

    if (event?.type === 'stock.empty' && event.stock) {
      this.ngZone.run(() => this.handleStockEmpty(event.stock!));
      return;
    }

    if (!event?.task) {
      return;
    }

    if (event.type === 'task.created') {
      this.ngZone.run(() => this.handleTaskCreated(event.task as EmployeeTask));
      return;
    }

    if (event.type === 'task.updated') {
      this.ngZone.run(() => this.handleTaskUpdated(event.task as EmployeeTask));
      return;
    }

  }

  private handleTaskCreated(task: EmployeeTask): void {
    this.productNotificationsService.addTaskNotification({
      id: `EMPLOYEE_TASK_ASSIGNED:${task.id}`,
      type: 'EMPLOYEE_TASK_ASSIGNED',
      taskId: task.id,
      title: 'Nueva tarea asignada',
      message: task.title,
      actionLabel: 'Ver mis tareas',
      routeCommands: ['/my-tasks'],
    });
    this.notificationSoundService.play();
    this.alertService.open({ type: 'info', message: `Nueva tarea: ${task.title}` });
    window.dispatchEvent(new CustomEvent<EmployeeTask>(EMPLOYEE_TASK_CREATED_EVENT, { detail: task }));
  }

  private handleTaskUpdated(task: EmployeeTask): void {
    this.productNotificationsService.addTaskNotification({
      id: `EMPLOYEE_TASK_ASSIGNED:${task.id}`,
      type: 'EMPLOYEE_TASK_ASSIGNED',
      taskId: task.id,
      title: 'Tarea actualizada',
      message: task.title,
      actionLabel: 'Ver mis tareas',
      routeCommands: ['/my-tasks'],
    });
    this.notificationSoundService.play();
    this.alertService.open({ type: 'info', message: `Tarea actualizada: ${task.title}` });
    window.dispatchEvent(new CustomEvent<EmployeeTask>(EMPLOYEE_TASK_UPDATED_EVENT, { detail: task }));
  }

  private handleStockEmpty(stock: { product_name: string; product_business_id: number; on_hand: number; min_stock?: number }): void {
    const isOutOfStock = Number(stock.on_hand ?? 0) <= 0;
    this.productNotificationsService.addStockNotification({
      id: `${isOutOfStock ? 'OUT_OF_STOCK' : 'LOW_STOCK'}:${stock.product_business_id}`,
      type: isOutOfStock ? 'OUT_OF_STOCK' : 'LOW_STOCK',
      productBusinessId: stock.product_business_id,
      title: isOutOfStock ? 'Stock agotado' : 'Stock bajo',
      message: isOutOfStock
        ? `"${stock.product_name}" se ha agotado. Existencia actual: ${stock.on_hand}.`
        : `"${stock.product_name}" quedó con stock bajo. Existencia actual: ${stock.on_hand}. Mínimo configurado: ${stock.min_stock ?? 0}.`,
      actionLabel: 'Ir a inventario',
      routeCommands: ['/inventory'],
    });
    this.notificationSoundService.play();
    this.alertService.open({
      type: 'warning',
      message: `${isOutOfStock ? 'Stock agotado' : 'Stock bajo'}: ${stock.product_name}`,
    });
  }

  private close(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.connectionVersion++;
    this.streamController?.abort();
    this.streamController = null;
  }
}
